import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, Spinner, Alert, Button, Table, Badge, Form, Row, Col, Nav, Tab, Modal, InputGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faArrowRight,
  faMobileAlt,
  faHistory,
  faMusic,
  faCopy,
  faTrashAlt,
  faSyncAlt,
  faCoins,
  faPlus,
  faMinus,
  faGift,
  faUserFriends,
  faSync,
  faExchangeAlt
} from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../../utils/setupAxios';
import './DeviceDetailBalance.scss';
import Pagination from '../common/Pagination';
import useDebounce from '../../hooks/useDebounce';

const TRANSACTION_PER_PAGE = 15;
const SONGS_PER_PAGE = 15;
const REFERRED_DEVICES_PER_PAGE = 15;

const DEFAULT_CLEAR_REWARD_OPTIONS = [
  { reward_type: 'starter_quest', reward_key: 'first_purchase', display_label: 'starter_quest → first_purchase' },
  { reward_type: 'starter_quest', reward_key: 'login_rewards', display_label: 'starter_quest → login_rewards' },
  { reward_type: 'daily_checkin', reward_key: 'all', display_label: 'daily_checkin → all' },
];

const buildClearRewardOptionId = (option) => `${option.reward_type}|${option.reward_key}`;

const parseClearRewardOptionId = (optionId) => {
  const [reward_type, ...rest] = optionId.split('|');
  return { reward_type, reward_key: rest.join('|') };
};

const DeviceDetail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const deviceId = searchParams.get('device_id') || '';

  const [device, setDevice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Referral (for admin testing)
  const [referral, setReferral] = useState({ loading: false, data: null, error: null });
  const [showReferralClaimModal, setShowReferralClaimModal] = useState(false);
  const [referralClaimCode, setReferralClaimCode] = useState('');
  const [referralClaimSubmitting, setReferralClaimSubmitting] = useState(false);
  const [referralClaimError, setReferralClaimError] = useState(null);
  const [referralClaimSuccess, setReferralClaimSuccess] = useState(null);
  const [referralClaimData, setReferralClaimData] = useState(null);

  const [transactionPage, setTransactionPage] = useState(1);
  const [transactionFilters, setTransactionFilters] = useState({ transaction_type: '', credit_type: '' });
  const [transactionPagination, setTransactionPagination] = useState({ current_page: 1, last_page: 1, per_page: TRANSACTION_PER_PAGE, total: 0 });

  const [songs, setSongs] = useState([]);
  const [songsLoading, setSongsLoading] = useState(false);
  const [songsError, setSongsError] = useState(null);
  const [songsPage, setSongsPage] = useState(1);
  const [songsPagination, setSongsPagination] = useState({ current_page: 1, last_page: 1, per_page: SONGS_PER_PAGE, total: 0 });
  const [songsFilters, setSongsFilters] = useState({ status: '', service: '', type: '', search: '', sort_by: 'created_at', sort_order: 'desc' });
  const debouncedSearch = useDebounce(songsFilters.search, 400);

  const [referredDevices, setReferredDevices] = useState([]);
  const [referredDevicesLoading, setReferredDevicesLoading] = useState(false);
  const [referredDevicesError, setReferredDevicesError] = useState(null);
  const [referredDevicesPage, setReferredDevicesPage] = useState(1);

  const [emailCopied, setEmailCopied] = useState(false);

  const [clearLoading, setClearLoading] = useState(false);
  const [clearMessage, setClearMessage] = useState(null);
  const [clearRewardOptionId, setClearRewardOptionId] = useState(
    buildClearRewardOptionId(DEFAULT_CLEAR_REWARD_OPTIONS[1])
  );
  const [clearRewardLoading, setClearRewardLoading] = useState(false);
  const [clearRewardMessage, setClearRewardMessage] = useState(null);
  const [clearRewardOptions, setClearRewardOptions] = useState(DEFAULT_CLEAR_REWARD_OPTIONS);

  const [legacyMigrateLoading, setLegacyMigrateLoading] = useState(false);
  const [legacyMigrateBanner, setLegacyMigrateBanner] = useState(null);
  const [initialCreditsLoading, setInitialCreditsLoading] = useState(false);
  const [initialCreditsBanner, setInitialCreditsBanner] = useState(null);

  // Update Credit (bonus) modal
  const [showUpdateCreditModal, setShowUpdateCreditModal] = useState(false);
  const [bonusTypes, setBonusTypes] = useState([]);
  const [bonusTypesLoading, setBonusTypesLoading] = useState(false);
  const [updateCreditForm, setUpdateCreditForm] = useState({ bonus_type: '', amount: '' });
  const [updateCreditSubmitting, setUpdateCreditSubmitting] = useState(false);
  const [updateCreditError, setUpdateCreditError] = useState(null);
  const [updateCreditSuccess, setUpdateCreditSuccess] = useState(null);

  const fetchDetail = useCallback(async () => {
    if (!deviceId) {
      setError('Missing device_id');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const params = {
        device_id: deviceId,
        transaction_page: transactionPage,
        transaction_per_page: TRANSACTION_PER_PAGE
      };
      if (transactionFilters.transaction_type) {
        params.transaction_type = transactionFilters.transaction_type;
      }
      if (transactionFilters.credit_type) {
        params.credit_type = transactionFilters.credit_type;
      }
      const res = await axiosInstance.get('/songs/devices/detail-by-device-id', { params });
      const data = res.data;
      if (data?.success && data?.data) {
        setDevice(data.data);
        const th = data.data?.transaction_history;
        if (th?.pagination) {
          setTransactionPagination({
            current_page: th.pagination.current_page ?? transactionPage,
            last_page: th.pagination.last_page ?? 1,
            per_page: th.pagination.per_page ?? TRANSACTION_PER_PAGE,
            total: th.pagination.total ?? 0
          });
        }
      } else if (data?.success === false) {
        setError(data?.message || 'Device not found');
        setDevice(null);
      } else {
        setDevice(null);
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to fetch device detail');
      setDevice(null);
    } finally {
      setLoading(false);
    }
  }, [deviceId, transactionPage, transactionFilters.transaction_type, transactionFilters.credit_type]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axiosInstance.get('/songs/rewards/clearable-options');
        const options = res.data?.data;
        if (!cancelled && Array.isArray(options) && options.length > 0) {
          setClearRewardOptions(options);
          setClearRewardOptionId((current) => {
            const validIds = options.map(buildClearRewardOptionId);
            return validIds.includes(current) ? current : validIds[0];
          });
        }
      } catch {
        // Keep default clearable reward options when API is unavailable.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const fetchReferral = useCallback(async () => {
    if (!deviceId) return;
    setReferral({ loading: true, data: null, error: null });
    try {
      const res = await axiosInstance.get('/songs/referral', { params: { device_id: deviceId } });
      const data = res.data;
      if (data?.success) {
        setReferral({ loading: false, data: data?.data ?? null, error: null });
      } else {
        setReferral({ loading: false, data: null, error: data?.message || 'Failed to fetch referral info' });
      }
    } catch (err) {
      setReferral({ loading: false, data: null, error: err.response?.data?.message || err.message || 'Failed to fetch referral info' });
    }
  }, [deviceId]);

  useEffect(() => {
    if (deviceId) fetchReferral();
  }, [deviceId, fetchReferral]);

  const fetchSongs = useCallback(async () => {
    if (!deviceId) return;
    setSongsLoading(true);
    setSongsError(null);
    try {
      const params = {
        device_id: deviceId,
        page: songsPage,
        items_per_page: SONGS_PER_PAGE,
        sort_by: songsFilters.sort_by,
        sort_order: songsFilters.sort_order
      };
      if (songsFilters.status) params.status = songsFilters.status;
      if (songsFilters.service) params.service = songsFilters.service;
      if (songsFilters.type) params.type = songsFilters.type;
      if (debouncedSearch) params.search = debouncedSearch;
      const res = await axiosInstance.get('/songs/devices/songs', { params });
      const data = res.data;
      if (data?.success && data?.data) {
        setSongs(data.data?.items || []);
        const extra = data?.extra || {};
        setSongsPagination({
          current_page: extra.current_page ?? songsPage,
          last_page: Math.ceil((extra.total || 0) / (extra.per_page || SONGS_PER_PAGE)) || 1,
          per_page: extra.per_page ?? SONGS_PER_PAGE,
          total: extra.total ?? 0
        });
      } else {
        setSongs([]);
      }
    } catch (err) {
      setSongsError(err.response?.data?.message || err.message || 'Failed to fetch songs');
      setSongs([]);
    } finally {
      setSongsLoading(false);
    }
  }, [deviceId, songsPage, songsFilters.status, songsFilters.service, songsFilters.type, debouncedSearch, songsFilters.sort_by, songsFilters.sort_order]);

  useEffect(() => {
    if (deviceId) fetchSongs();
  }, [deviceId, fetchSongs]);

  const fetchReferredDevices = useCallback(async () => {
    if (!deviceId) return;
    setReferredDevicesLoading(true);
    setReferredDevicesError(null);
    try {
      const res = await axiosInstance.get('/songs/devices/referred-devices', { params: { device_id: deviceId } });
      const data = res.data;
      if (data?.success && data?.data) {
        setReferredDevices(data.data?.referred_devices || []);
        setReferredDevicesPage(1);
      } else {
        setReferredDevices([]);
      }
    } catch (err) {
      setReferredDevicesError(err.response?.data?.message || err.message || 'Failed to fetch referred devices');
      setReferredDevices([]);
    } finally {
      setReferredDevicesLoading(false);
    }
  }, [deviceId]);

  useEffect(() => {
    if (deviceId) fetchReferredDevices();
  }, [deviceId, fetchReferredDevices]);

  const formatDate = (val) => {
    if (val == null || val === '') return '—';
    try {
      // Server may send Unix timestamp in seconds; Date expects milliseconds
      const ms = typeof val === 'number' && val < 1e12 ? val * 1000 : val;
      const d = new Date(ms);
      return isNaN(d.getTime()) ? val : d.toLocaleString();
    } catch (_) {
      return val;
    }
  };

  const copyToClipboard = (text) => {
    if (typeof navigator?.clipboard?.writeText === 'function') {
      navigator.clipboard.writeText(text);
    }
  };

  const openReferralClaimModal = () => {
    setReferralClaimCode('');
    setReferralClaimSubmitting(false);
    setReferralClaimError(null);
    setReferralClaimSuccess(null);
    setReferralClaimData(null);
    setShowReferralClaimModal(true);
  };

  const closeReferralClaimModal = () => {
    setShowReferralClaimModal(false);
  };

  const mapReferralClaimError = (payload) => {
    const code = payload?.data?.code;
    if (code === 'ALREADY_CLAIMED') return 'Already claimed a referral code';
    if (code === 'INVALID_CODE') return 'Invalid referral code';
    if (code === 'SELF_REFERRAL') return 'Self referral is not allowed';
    return payload?.message || 'Failed to claim referral code';
  };

  const handleReferralClaimSubmit = async (e) => {
    e.preventDefault();
    if (!deviceId || !referralClaimCode.trim() || referralClaimSubmitting) return;
    setReferralClaimSubmitting(true);
    setReferralClaimError(null);
    setReferralClaimSuccess(null);
    setReferralClaimData(null);
    try {
      const res = await axiosInstance.post('/songs/referral/claim', {
        device_id: deviceId,
        code: referralClaimCode.trim()
      });
      const data = res.data;
      if (data?.success) {
        setReferralClaimSuccess(data?.message || 'Referral claimed successfully');
        setReferralClaimData(data?.data ?? null);
        fetchDetail();
        fetchReferral();
      } else {
        setReferralClaimError(mapReferralClaimError(data));
        setReferralClaimData(data?.data ?? null);
      }
    } catch (err) {
      const payload = err.response?.data;
      setReferralClaimError(payload ? mapReferralClaimError(payload) : (err.message || 'Failed to claim referral code'));
      setReferralClaimData(payload?.data ?? null);
    } finally {
      setReferralClaimSubmitting(false);
    }
  };

  const renderClaimBalance = (label, b) => {
    if (!b) return null;
    return (
      <div className="mb-3">
        <div className="text-muted mb-1">{label}</div>
        <Table bordered size="sm" className="mb-0">
          <tbody>
            <tr>
              <td style={{ width: 160 }} className="text-muted">device_id</td>
              <td style={{ wordBreak: 'break-word' }}><code className="small">{b.device_id || '—'}</code></td>
            </tr>
            <tr>
              <td className="text-muted">bonus_credits</td>
              <td>{b.bonus_credits ?? 0}</td>
            </tr>
            <tr>
              <td className="text-muted">subscription_credits</td>
              <td>{b.subscription_credits ?? 0}</td>
            </tr>
            <tr>
              <td className="text-muted">payg_credits</td>
              <td>{b.payg_credits ?? 0}</td>
            </tr>
            <tr>
              <td className="text-muted">total_credits</td>
              <td>{b.total_credits ?? 0}</td>
            </tr>
          </tbody>
        </Table>
      </div>
    );
  };

  const handleRetryLegacySubscriptionMigration = async () => {
    if (!deviceId || legacyMigrateLoading || initialCreditsLoading || clearLoading) return;
    if (
      !window.confirm(
        'Retry legacy subscription migration for this device? This calls the admin credits migration job for legacy subscriptions.'
      )
    ) {
      return;
    }
    setLegacyMigrateLoading(true);
    setLegacyMigrateBanner(null);
    try {
      const body = new URLSearchParams({ device_id: deviceId });
      const res = await axiosInstance.post(
        '/songs/admin/credits/retry-legacy-subscription-migration',
        body,
        { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
      );
      const data = res.data;
      if (data?.success) {
        setLegacyMigrateBanner({
          variant: 'success',
          message: data?.message || 'Legacy subscription migration request completed.',
        });
        fetchDetail();
        fetchSongs();
      } else {
        setLegacyMigrateBanner({
          variant: 'danger',
          message: data?.message || 'Migration request did not succeed.',
        });
      }
    } catch (err) {
      setLegacyMigrateBanner({
        variant: 'danger',
        message: err.response?.data?.message || err.message || 'Failed to run legacy subscription migration.',
      });
    } finally {
      setLegacyMigrateLoading(false);
    }
  };

  const handleInitialCredits = async () => {
    if (!deviceId || initialCreditsLoading || legacyMigrateLoading || clearLoading) return;
    if (
      !window.confirm(
        'Run initial credits / bonus for this device? This calls the credits initial endpoint for this device.'
      )
    ) {
      return;
    }
    setInitialCreditsLoading(true);
    setInitialCreditsBanner(null);
    try {
      const fd = new FormData();
      fd.append('device_id', deviceId);
      const res = await axiosInstance.post('/songs/credits/initial', fd);
      const data = res.data;
      if (data?.success) {
        setInitialCreditsBanner({
          variant: 'success',
          message: data?.message || 'Initial credits applied.',
        });
        fetchDetail();
        fetchSongs();
      } else {
        setInitialCreditsBanner({
          variant: 'danger',
          message: data?.message || 'Initial credits request did not succeed.',
        });
      }
    } catch (err) {
      setInitialCreditsBanner({
        variant: 'danger',
        message: err.response?.data?.message || err.message || 'Failed to run initial credits.',
      });
    } finally {
      setInitialCreditsLoading(false);
    }
  };

  const selectedClearRewardOption = clearRewardOptions.find(
    (option) => buildClearRewardOptionId(option) === clearRewardOptionId
  ) || parseClearRewardOptionId(clearRewardOptionId);

  const selectedClearRewardLabel = selectedClearRewardOption.display_label
    || `${selectedClearRewardOption.reward_type} → ${selectedClearRewardOption.reward_key}`;

  const handleClearReward = async () => {
    if (!deviceId || !clearRewardOptionId || clearRewardLoading) return;
    const { reward_type: rewardType, reward_key: rewardKey } = parseClearRewardOptionId(clearRewardOptionId);
    if (!rewardType || !rewardKey) return;
    if (!window.confirm(
      `Clear "${selectedClearRewardLabel}" for this device? Claim record, ledger history, and granted bonus credits will be removed.`
    )) return;
    setClearRewardLoading(true);
    setClearRewardMessage(null);
    try {
      const res = await axiosInstance.post('/songs/rewards/clear-claim', {
        device_id: deviceId,
        reward_type: rewardType,
        reward_key: rewardKey,
      });
      const data = res.data;
      if (data?.success && data?.data) {
        const d = data.data;
        const checkinPart = (d.checkins_deleted ?? 0) > 0 ? `, ${d.checkins_deleted} check-in(s)` : '';
        setClearRewardMessage(
          d.cleared
            ? `Cleared ${selectedClearRewardLabel}: ${d.claims_deleted ?? 0} claim(s)${checkinPart}, ${d.transactions_deleted ?? 0} transaction(s), ${d.credits_reversed ?? 0} bonus credit(s) reversed.`
            : (data?.message || `No ${selectedClearRewardLabel} record found.`)
        );
        fetchDetail();
      } else {
        setClearRewardMessage(data?.message || 'Done');
      }
    } catch (err) {
      setClearRewardMessage(err.response?.data?.message || err.message || 'Failed to clear reward');
    } finally {
      setClearRewardLoading(false);
    }
  };

  const handleClearTestData = async () => {
    if (!deviceId || clearLoading || legacyMigrateLoading || initialCreditsLoading) return;
    if (!window.confirm('Clear test data (subscriptions & transactions) for this device? Bonus credits will be kept.')) return;
    setClearLoading(true);
    setClearMessage(null);
    try {
      const res = await axiosInstance.post('/songs/devices/clear-test-data', { device_id: deviceId });
      const data = res.data;
      if (data?.success && data?.data) {
        const d = data.data;
        setClearMessage(
          `Cleared: ${d.subscriptions_deleted ?? 0} subscription(s), ${d.transactions_deleted ?? 0} transaction(s). Bonus credits kept: ${d.bonus_credits_kept ?? 0}`
        );
        fetchDetail();
        fetchSongs();
      } else {
        setClearMessage(data?.message || 'Done');
      }
    } catch (err) {
      setClearMessage(err.response?.data?.message || err.message || 'Failed to clear test data');
    } finally {
      setClearLoading(false);
    }
  };

  const fetchBonusTypes = useCallback(async () => {
    setBonusTypesLoading(true);
    try {
      const res = await axiosInstance.get('/songs/devices/bonus-credit-types');
      const data = res.data;
      if (data?.success && Array.isArray(data?.data)) {
        setBonusTypes(data.data);
        if (data.data.length > 0) {
          setUpdateCreditForm((f) => ({ ...f, bonus_type: data.data[0].value }));
        }
      }
    } catch (err) {
      setUpdateCreditError(err.response?.data?.message || err.message || 'Failed to load bonus types');
    } finally {
      setBonusTypesLoading(false);
    }
  }, []);

  const openUpdateCreditModal = () => {
    setUpdateCreditForm({ bonus_type: '', amount: '' });
    setUpdateCreditError(null);
    setUpdateCreditSuccess(null);
    setShowUpdateCreditModal(true);
    fetchBonusTypes();
  };

  const closeUpdateCreditModal = () => {
    setShowUpdateCreditModal(false);
    setUpdateCreditForm({ bonus_type: '', amount: '' });
    setUpdateCreditError(null);
    setUpdateCreditSuccess(null);
  };

  const setUpdateCreditAmount = (value) => {
    const num = parseInt(value, 10);
    if (value === '' || value === '-') {
      setUpdateCreditForm((f) => ({ ...f, amount: value }));
      return;
    }
    if (!Number.isNaN(num)) setUpdateCreditForm((f) => ({ ...f, amount: String(num) }));
  };

  const handleAdjustBonusCreditSubmit = async (e) => {
    e.preventDefault();
    setUpdateCreditError(null);
    const amountNum = parseInt(updateCreditForm.amount, 10);
    if (!updateCreditForm.bonus_type) {
      setUpdateCreditError('Please select a bonus type.');
      return;
    }
    if (updateCreditForm.amount === '' || updateCreditForm.amount === '-') {
      setUpdateCreditError('Please enter an amount.');
      return;
    }
    if (Number.isNaN(amountNum) || amountNum === 0) {
      setUpdateCreditError('Amount must not be zero. Use a positive number to add or negative to subtract.');
      return;
    }
    setUpdateCreditSubmitting(true);
    try {
      const res = await axiosInstance.post('/songs/devices/adjust-bonus-credit', {
        device_id: deviceId,
        bonus_type: updateCreditForm.bonus_type,
        amount: amountNum
      });
      const data = res.data;
      if (data?.success) {
        setUpdateCreditSuccess(
          data?.data?.transaction_created
            ? `Bonus adjusted. New bonus: ${data.data.bonus_credits ?? '—'}, total: ${data.data.total_credits ?? '—'} credits.`
            : 'Bonus credits unchanged (no transaction created).'
        );
        fetchDetail();
        setTimeout(() => {
          closeUpdateCreditModal();
        }, 1500);
      } else {
        setUpdateCreditError(data?.message || 'Failed to adjust bonus credit');
      }
    } catch (err) {
      setUpdateCreditError(err.response?.data?.message || err.message || 'Failed to adjust bonus credit');
    } finally {
      setUpdateCreditSubmitting(false);
    }
  };

  const transactions = device?.transaction_history?.items || [];
  const balance = device?.balance;
  const referralCode = referral.data?.referral_code;
  const hasUsedReferral = Boolean(referral.data?.has_used_referral);
  const referredByAliasId = referral.data?.referred_by_alias_id ?? null;

  if (!deviceId) {
    return (
      <div>
        <Alert variant="warning">
          Missing device_id. <Button variant="link" className="p-0" onClick={() => navigate('/devices')}>Back to Devices</Button>
        </Alert>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <Button variant="outline-secondary" size="sm" className="mb-2" onClick={() => navigate('/devices')}>
            <FontAwesomeIcon icon={faArrowLeft} className="me-1" />
            Back to Devices
          </Button>
          <h2 className="mb-0">
            <FontAwesomeIcon icon={faMobileAlt} className="me-2" />
            Device Detail
          </h2>
        </div>
        <div className="d-flex gap-2">
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => { fetchDetail(); fetchSongs(); fetchReferral(); fetchReferredDevices(); }}
            disabled={loading}
            title="Reload device & subscription data"
          >
            <FontAwesomeIcon icon={faSyncAlt} className={loading ? 'fa-spin' : ''} />
            <span className="ms-1">{loading ? 'Reloading...' : 'Reload'}</span>
          </Button>
          <Button
            variant="outline-warning"
            size="sm"
            onClick={openReferralClaimModal}
            disabled={referral.loading || hasUsedReferral}
            title="Claim a referral code for this device"
          >
            <FontAwesomeIcon icon={faGift} className="me-1" />
            Add referral code
          </Button>
          <Button
            variant="outline-success"
            size="sm"
            onClick={openUpdateCreditModal}
            title="Adjust bonus credit (add or subtract)"
          >
            <FontAwesomeIcon icon={faCoins} className="me-1" />
            Update Credit
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={handleRetryLegacySubscriptionMigration}
            disabled={loading || legacyMigrateLoading || initialCreditsLoading || clearLoading}
            title="Retry legacy subscription migration (admin credits)"
          >
            <FontAwesomeIcon icon={faSync} className="me-1" spin={legacyMigrateLoading} />
            {legacyMigrateLoading ? 'Migrating…' : 'Migrate legacy'}
          </Button>
          <Button
            variant="outline-info"
            size="sm"
            onClick={handleInitialCredits}
            disabled={loading || legacyMigrateLoading || initialCreditsLoading || clearLoading}
            title="Initial credits / bonus for this device"
          >
            <FontAwesomeIcon icon={faPlus} className="me-1" spin={initialCreditsLoading} />
            {initialCreditsLoading ? 'Applying…' : 'Initial bonus'}
          </Button>
          <Button
            variant="outline-danger"
            size="sm"
            onClick={handleClearTestData}
            disabled={clearLoading || legacyMigrateLoading || initialCreditsLoading}
          >
            <FontAwesomeIcon icon={faTrashAlt} className="me-1" />
            {clearLoading ? 'Clearing...' : 'Clear subscriptions'}
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="danger" className="mb-3" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {clearMessage && (
        <Alert variant={clearMessage.startsWith('Failed') ? 'danger' : 'success'} className="mb-3" dismissible onClose={() => setClearMessage(null)}>
          {clearMessage}
        </Alert>
      )}
      {legacyMigrateBanner && (
        <Alert
          variant={legacyMigrateBanner.variant}
          className="mb-3"
          dismissible
          onClose={() => setLegacyMigrateBanner(null)}
        >
          {legacyMigrateBanner.message}
        </Alert>
      )}
      {initialCreditsBanner && (
        <Alert
          variant={initialCreditsBanner.variant}
          className="mb-3"
          dismissible
          onClose={() => setInitialCreditsBanner(null)}
        >
          {initialCreditsBanner.message}
        </Alert>
      )}

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" />
          <span className="ms-2">Loading device...</span>
        </div>
      ) : !device ? (
        <Alert variant="warning">
          Device not found. <Button variant="link" className="p-0" onClick={() => navigate('/devices')}>Back to Devices</Button>
        </Alert>
      ) : (
        <>
          <Card className="mb-4">
            <Card.Header>
              <strong>Device ID</strong>
              <code className="ms-2 small" style={{ wordBreak: 'break-all' }}>{device.device_id}</code>
              <Button variant="outline-secondary" size="sm" className="ms-2" onClick={() => copyToClipboard(device.device_id)} title="Copy">
                <FontAwesomeIcon icon={faCopy} />
              </Button>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={8}>
                  <p><strong>Platform:</strong> <Badge bg="light" className="text-dark">{device.platform || '—'}</Badge></p>
                  <p><strong>Language:</strong> {device.device_lang || '—'}</p>
                  <p><strong>Subscribed:</strong> {device.subscribed ? <Badge bg="success">Yes</Badge> : <Badge bg="light" className="text-dark">No</Badge>}</p>
                  <p><strong>Subscription end:</strong> {formatDate(device.subscription_end)}</p>
                  {device.current_plan_name != null && (
                    <>
                      <p><strong>Current plan:</strong> <Badge bg="primary" className="text-white">{device.current_plan_name}</Badge></p>
                      <p><strong>Current product:</strong> <code className="small">{device.current_product_id || '—'}</code></p>
                      <p><strong>Next renewal:</strong> {formatDate(device.subscription_next_renewal_date)}</p>
                    </>
                  )}
                  <p><strong>Notification permission:</strong> {device.notification_permission ? 'Yes' : 'No'}</p>
                  <p><strong>FCM token:</strong> {device.fcm_token ? <code className="small" style={{ wordBreak: 'break-all' }}>{device.fcm_token}</code> : '—'}</p>
                  <p>
                    <strong>Logged in:</strong>{' '}
                    {device.is_logged_in ? <Badge bg="success" className="text-white">Yes</Badge> : <Badge bg="light" className="text-dark">No</Badge>}
                  </p>
                  <p>
                    <strong>Purchased:</strong>{' '}
                    {device.is_purchased == null
                      ? <Badge bg="light" className="text-dark">—</Badge>
                      : device.is_purchased
                        ? <Badge bg="success" className="text-white">Yes</Badge>
                        : <Badge bg="light" className="text-dark">No</Badge>
                    }
                  </p>
                  {device.linked_user && (
                    <div className="mt-2 mb-2 p-2 border rounded" style={{ lineHeight: '2' }}>
                      <strong>Linked user</strong>
                      <div className="mt-1 small">
                        <div><span className="text-muted">Name:</span> <span className="ms-1">{device.linked_user.name || '—'}</span></div>
                        <div className="d-flex align-items-center gap-2">
                          <span className="text-muted">Email:</span>
                          {device.linked_user.email ? (
                            <span className="d-flex align-items-center gap-1">
                              <Button
                                variant="link"
                                size="sm"
                                className="p-0 text-body fw-normal"
                                style={{ fontSize: 'inherit' }}
                                onClick={() => {
                                  copyToClipboard(device.linked_user.email);
                                  setEmailCopied(true);
                                  setTimeout(() => setEmailCopied(false), 2000);
                                }}
                              >
                                {device.linked_user.email}
                              </Button>
                              <FontAwesomeIcon icon={faCopy} className="text-muted" style={{ fontSize: '0.75rem' }} />
                              {emailCopied && <Badge bg="success" className="text-white" style={{ fontSize: '0.7rem' }}>Copied!</Badge>}
                            </span>
                          ) : '—'}
                        </div>
                        <div><span className="text-muted">Alias ID:</span> <code className="ms-1">{device.linked_user.alias_id || '—'}</code></div>
                        <div><span className="text-muted">Provider:</span> <Badge bg="secondary" className="text-white ms-1">{device.linked_user.provider || '—'}</Badge></div>
                        {device.linked_user.alias_id && (
                          <div>
                            <a
                              href={`https://song.appixi.net/songs/share/users/${device.linked_user.alias_id}`}
                              target="_blank"
                              rel="noreferrer"
                            >
                              List Songs
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="mt-3 pt-3 border-top">
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                      <strong>Referral</strong>
                      {referral.loading ? (
                        <Spinner animation="border" size="sm" />
                      ) : referral.error ? (
                        <Badge bg="danger">Error</Badge>
                      ) : referral.data ? (
                        <Badge bg="success">Loaded</Badge>
                      ) : (
                        <Badge bg="secondary">—</Badge>
                      )}
                    </div>
                    {referral.error ? (
                      <Alert variant="danger" className="mt-2 mb-0">
                        {referral.error}
                      </Alert>
                    ) : (
                      <>
                        <div className="d-flex align-items-center gap-2 mt-2 flex-wrap">
                          <span className="text-muted">referral_code:</span>
                          <code className="small fw-semibold">{referralCode || '—'}</code>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            disabled={!referralCode}
                            onClick={() => copyToClipboard(referralCode)}
                            title="Copy referral code"
                          >
                            <FontAwesomeIcon icon={faCopy} />
                          </Button>
                        </div>
                        <div className="d-flex gap-2 flex-wrap mt-2">
                          <Badge bg={hasUsedReferral ? 'warning' : 'info'} text={hasUsedReferral ? 'dark' : 'white'}>
                            has_used_referral: {hasUsedReferral ? 'true' : 'false'}
                          </Badge>
                          <Badge bg={referredByAliasId ? 'secondary' : 'light'} text={referredByAliasId ? 'white' : 'dark'}>
                            referred_by_alias_id: {referredByAliasId || 'null'}
                          </Badge>
                        </div>
                      </>
                    )}
                  </div>

                  {device.transfer_info && (
                    <div className="mt-3 pt-3 border-top">
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <FontAwesomeIcon icon={faExchangeAlt} className="text-warning" />
                        <strong>Transfer history</strong>
                        <Badge bg={device.transfer_info.status === 'active' ? 'success' : 'secondary'}>
                          {device.transfer_info.status}
                        </Badge>
                      </div>
                      {device.transfer_info.status === 'deactive' && device.transfer_info.transferred_to && (
                        <div className="small p-2 rounded border border-warning bg-warning bg-opacity-10">
                          <FontAwesomeIcon icon={faArrowRight} className="text-warning me-1" />
                          <span className="text-muted me-1">Transferred to:</span>
                          <a
                            href={`/devices/detail?device_id=${encodeURIComponent(device.transfer_info.transferred_to.device_id)}`}
                            onClick={(e) => { e.preventDefault(); navigate(`/devices/detail?device_id=${encodeURIComponent(device.transfer_info.transferred_to.device_id)}`); }}
                          >
                            <code className="small">{device.transfer_info.transferred_to.device_id}</code>
                          </a>
                          {device.transfer_info.transferred_to.transferred_at && (
                            <span className="text-muted ms-2">({formatDate(device.transfer_info.transferred_to.transferred_at)})</span>
                          )}
                        </div>
                      )}
                      {device.transfer_info.status === 'active' && device.transfer_info.transferred_from && device.transfer_info.transferred_from.length > 0 && (
                        <div className="d-flex flex-column gap-1">
                          {device.transfer_info.transferred_from.map((tf, idx) => (
                            <div key={idx} className="small p-2 rounded border">
                              <FontAwesomeIcon icon={faArrowLeft} className="text-success me-1" />
                              <span className="text-muted me-1">Transferred from:</span>
                              <a
                                href={`/devices/detail?device_id=${encodeURIComponent(tf.device_id)}`}
                                onClick={(e) => { e.preventDefault(); navigate(`/devices/detail?device_id=${encodeURIComponent(tf.device_id)}`); }}
                              >
                                <code className="small">{tf.device_id}</code>
                              </a>
                              {tf.transferred_at && (
                                <span className="text-muted ms-2">({formatDate(tf.transferred_at)})</span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                      {device.transfer_info.status === 'active' && (!device.transfer_info.transferred_from || device.transfer_info.transferred_from.length === 0) && (
                        <div className="small text-muted">No transfers recorded for this device.</div>
                      )}
                    </div>
                  )}

                  {device.queued_plans && device.queued_plans.length > 0 && (
                    <div className="mt-3">
                      <strong>Queued plans (upcoming):</strong>
                      <ul className="list-unstyled mb-0 mt-1 small">
                        {device.queued_plans.map((q, idx) => (
                          <li key={idx} className="border-start border-2 border-info ps-2 mb-2">
                            <span className="fw-semibold">{q.plan_name}</span>
                            {q.product_id && <code className="d-block small text-muted">{q.product_id}</code>}
                            {(q.credits != null || q.credits_unit) && (
                              <span className="d-block">
                                Credits: {q.credits != null ? q.credits.toLocaleString() : '—'}
                                {q.credits_unit ? `/${q.credits_unit}` : ''}
                              </span>
                            )}
                            {q.effective_from != null && <span className="d-block">Effective from: {formatDate(q.effective_from)}</span>}
                            {q.next_renewal_date != null && <span className="d-block">Next renewal: {formatDate(q.next_renewal_date)}</span>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </Col>
                <Col md={4}>
                  <Card className="device-balance-card shadow-sm rounded-3 border" aria-label="Total balance card">
                    <Card.Body className="pt-3 pb-3 px-3">
                      <div className="d-flex flex-column gap-1 mb-3">
                        <span className="small text-muted fw-semibold">Total balance</span>
                        <div className="d-flex align-items-baseline gap-2 flex-wrap">
                          <span className="display-5 fw-bold text-primary">
                            {(balance && typeof balance === 'object') ? (balance.total ?? 0) : (device.balance ?? 0)}
                          </span>
                          <span className="small text-muted fw-semibold">credits</span>
                        </div>
                      </div>
                      {(balance && typeof balance === 'object') ? (
                        <div className="d-flex flex-column gap-2 pt-2 border-top">
                          <div className="d-flex align-items-center gap-2 py-1">
                            <span className="text-body">Bonus</span>
                            <span className="fw-bold">{balance.bonus_credits ?? 0}</span>
                          </div>
                          <div className="d-flex align-items-center gap-2 py-1">
                            <span className="text-body">Subscription</span>
                            <span className="fw-bold">{balance.subscription_credits ?? 0}</span>
                          </div>
                          <div className="d-flex align-items-center gap-2 py-1">
                            <span className="text-body">PAYG</span>
                            <span className="fw-bold">{balance.payg_credits ?? 0}</span>
                          </div>
                        </div>
                      ) : (
                        <div className="d-flex align-items-center gap-2 py-1 pt-2 border-top">
                          <span className="text-body">Total</span>
                          <span className="fw-bold">{device.balance ?? 0}</span>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                  <p className="mt-2 mb-0"><strong>Created:</strong> {formatDate(device.created_at)}</p>
                  <p className="mb-0"><strong>Updated:</strong> {formatDate(device.updated_at)}</p>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <Tab.Container defaultActiveKey="transactions">
            <Nav variant="tabs" className="nav-tabs-scroll">
              <Nav.Item>
                <Nav.Link eventKey="transactions">
                  <FontAwesomeIcon icon={faHistory} className="me-1" />
                  Transaction history
                  {transactionPagination.total > 0 && (
                    <Badge bg="light" className="ms-1 text-dark">{transactionPagination.total}</Badge>
                  )}
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="songs">
                  <FontAwesomeIcon icon={faMusic} className="me-1" />
                  Songs
                  {songsPagination.total > 0 && (
                    <Badge bg="light" className="ms-1 text-dark">{songsPagination.total}</Badge>
                  )}
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="referred-devices">
                  <FontAwesomeIcon icon={faUserFriends} className="me-1" />
                  Referred devices
                  {referredDevices.length > 0 && (
                    <Badge bg="light" className="ms-1 text-dark">{referredDevices.length}</Badge>
                  )}
                </Nav.Link>
              </Nav.Item>
            </Nav>
            <Tab.Content className="pt-3">
              <Tab.Pane eventKey="transactions">
                <Card>
                  <Card.Body>
                    <Row className="mb-3 g-2 align-items-center">
                      <Col md={3}>
                        <Form.Select
                          value={transactionFilters.transaction_type}
                          onChange={(e) => {
                            setTransactionFilters((f) => ({ ...f, transaction_type: e.target.value }));
                            setTransactionPage(1);
                          }}
                        >
                          <option value="">All transaction types</option>
                          <option value="deduct">deduct</option>
                          <option value="grant">grant</option>
                          <option value="reset">reset</option>
                        </Form.Select>
                      </Col>
                      <Col md={3}>
                        <Form.Select
                          value={transactionFilters.credit_type}
                          onChange={(e) => {
                            setTransactionFilters((f) => ({ ...f, credit_type: e.target.value }));
                            setTransactionPage(1);
                          }}
                        >
                          <option value="">All credit types</option>
                          <option value="bonus">bonus</option>
                          <option value="subscription">subscription</option>
                          <option value="payg">payg</option>
                        </Form.Select>
                      </Col>
                      <Col md={6} lg={5}>
                        <div className="d-flex gap-2">
                          <Form.Select
                            className="flex-grow-1"
                            value={clearRewardOptionId}
                            onChange={(e) => setClearRewardOptionId(e.target.value)}
                            disabled={clearRewardLoading}
                            aria-label="Reward to clear"
                          >
                            {clearRewardOptions.map((option) => (
                              <option key={buildClearRewardOptionId(option)} value={buildClearRewardOptionId(option)}>
                                {option.display_label}
                              </option>
                            ))}
                          </Form.Select>
                          <Button
                            variant="outline-warning"
                            onClick={handleClearReward}
                            disabled={clearRewardLoading || !clearRewardOptionId}
                            title="Clear selected reward claim for testing"
                          >
                            <FontAwesomeIcon icon={faTrashAlt} className="me-1" />
                            {clearRewardLoading ? 'Clearing...' : 'Clear reward'}
                          </Button>
                        </div>
                      </Col>
                    </Row>
                    {clearRewardMessage && (
                      <Alert
                        variant={clearRewardMessage.startsWith('Failed') ? 'danger' : 'success'}
                        className="mb-3 py-2"
                        dismissible
                        onClose={() => setClearRewardMessage(null)}
                      >
                        {clearRewardMessage}
                      </Alert>
                    )}
                    <Pagination
                      currentPage={transactionPagination.current_page}
                      totalItems={transactionPagination.total}
                      itemsPerPage={transactionPagination.per_page}
                      onPageChange={setTransactionPage}
                      position="top"
                    />
                    <Table responsive hover size="sm">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Type</th>
                          <th>Credit</th>
                          <th>Amount</th>
                          <th>Before</th>
                          <th>After</th>
                          <th>Description</th>
                          <th>Created</th>
                        </tr>
                      </thead>
                      <tbody>
                        {transactions.length === 0 ? (
                          <tr>
                            <td colSpan="8" className="text-center text-muted py-3">No transactions.</td>
                          </tr>
                        ) : (
                          transactions.map((t) => (
                            <tr key={t.id}>
                              <td>{t.id}</td>
                              <td><Badge bg={t.transaction_type === 'deduct' ? 'warning' : 'success'} text={t.transaction_type === 'deduct' ? 'dark' : undefined}>{t.transaction_type}</Badge></td>
                              <td><Badge bg="light" className="text-dark">{t.credit_type}</Badge></td>
                              <td>{t.amount}</td>
                              <td>{t.balance_before}</td>
                              <td>{t.balance_after}</td>
                              <td>{t.description || '—'}</td>
                              <td>{formatDate(t.created_at)}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                    <Pagination
                      currentPage={transactionPagination.current_page}
                      totalItems={transactionPagination.total}
                      itemsPerPage={transactionPagination.per_page}
                      onPageChange={setTransactionPage}
                      position="bottom"
                    />
                  </Card.Body>
                </Card>
              </Tab.Pane>
              <Tab.Pane eventKey="songs">
                <Card>
                  <Card.Body>
                    <Row className="mb-3 g-2 align-items-center songs-filter-row">
                      <Col md={2}>
                        <Form.Select
                          className="form-control songs-filter-input"
                          value={songsFilters.status}
                          onChange={(e) => { setSongsFilters((f) => ({ ...f, status: e.target.value })); setSongsPage(1); }}
                        >
                          <option value="">All status</option>
                          <option value="open">open</option>
                          <option value="in_progress">in_progress</option>
                          <option value="done">done</option>
                          <option value="failed">failed</option>
                        </Form.Select>
                      </Col>
                      <Col md={2}>
                        <Form.Select
                          className="form-control songs-filter-input"
                          value={songsFilters.service}
                          onChange={(e) => { setSongsFilters((f) => ({ ...f, service: e.target.value })); setSongsPage(1); }}
                        >
                          <option value="">All service</option>
                          <option value="suno">suno</option>
                          <option value="apihub">apihub</option>
                          <option value="musicgpt">musicgpt</option>
                          <option value="kie">kie</option>
                        </Form.Select>
                      </Col>
                      <Col md={2}>
                        <Form.Select
                          className="form-control songs-filter-input"
                          value={songsFilters.type}
                          onChange={(e) => { setSongsFilters((f) => ({ ...f, type: e.target.value })); setSongsPage(1); }}
                        >
                          <option value="">All type</option>
                          <option value="prompt">prompt</option>
                          <option value="lyrics">lyrics</option>
                          <option value="cover">cover</option>
                          <option value="lipsync">lipsync</option>
                          <option value="voice_model">voice_model</option>
                          <option value="extend_lyrics">extend_lyrics</option>
                          <option value="extend_prompt">extend_prompt</option>
                        </Form.Select>
                      </Col>
                      <Col md={2}>
                        <Form.Select
                          className="form-control songs-filter-input"
                          value={songsFilters.sort_by}
                          onChange={(e) => setSongsFilters((f) => ({ ...f, sort_by: e.target.value }))}
                        >
                          <option value="created_at">created_at</option>
                          <option value="updated_at">updated_at</option>
                          <option value="task_id">task_id</option>
                          <option value="status">status</option>
                          <option value="service">service</option>
                          <option value="type">type</option>
                        </Form.Select>
                      </Col>
                      <Col md={1}>
                        <Form.Select
                          className="form-control songs-filter-input"
                          value={songsFilters.sort_order}
                          onChange={(e) => setSongsFilters((f) => ({ ...f, sort_order: e.target.value }))}
                        >
                          <option value="desc">desc</option>
                          <option value="asc">asc</option>
                        </Form.Select>
                      </Col>
                      <Col md={3}>
                        <Form.Control
                          size="sm"
                          type="text"
                          placeholder="Search title..."
                          className="form-control songs-filter-input"
                          value={songsFilters.search}
                          onChange={(e) => setSongsFilters((f) => ({ ...f, search: e.target.value }))}
                        />
                      </Col>
                    </Row>
                    {songsError && <Alert variant="danger">{songsError}</Alert>}
                    {songsLoading ? (
                      <div className="text-center py-4"><Spinner animation="border" /> Loading songs...</div>
                    ) : (
                      <>
                        <Pagination
                          currentPage={songsPagination.current_page}
                          totalItems={songsPagination.total}
                          itemsPerPage={songsPagination.per_page}
                          onPageChange={setSongsPage}
                          position="top"
                        />
                        <Table responsive hover size="sm">
                          <thead>
                            <tr>
                              <th>ID</th>
                              <th>Task ID</th>
                              <th>Title</th>
                              <th>Status</th>
                              <th>Service</th>
                              <th>Type</th>
                              <th>Created</th>
                              <th>Updated</th>
                            </tr>
                          </thead>
                          <tbody>
                            {songs.length === 0 ? (
                              <tr>
                                <td colSpan="8" className="text-center text-muted py-3">No songs.</td>
                              </tr>
                            ) : (
                              songs.map((s, idx) => {
                                const rowNum = (songsPagination.current_page - 1) * (songsPagination.per_page || SONGS_PER_PAGE) + idx + 1;
                                return (
                                  <tr key={s.task_id || idx}>
                                    <td>{s.id ?? rowNum}</td>
                                    <td><code className="small">{s.task_id || '—'}</code></td>
                                    <td>{s.title || '—'}</td>
                                    <td><Badge bg={s.status === 'done' ? 'success' : s.status === 'failed' ? 'danger' : 'warning'}>{s.status}</Badge></td>
<td><Badge bg="warning" className="text-dark">{s.service || '—'}</Badge></td>
                                    <td><Badge bg="secondary" className="text-white" style={{ padding: '0.35rem 0.65rem', fontSize: '0.875rem' }}>{s.type || '—'}</Badge></td>
                                    <td>{formatDate(s.create_time ?? s.created_at)}</td>
                                    <td>{formatDate(s.update_time ?? s.updated_at)}</td>
                                  </tr>
                                );
                              })
                            )}
                          </tbody>
                        </Table>
                        <Pagination
                          currentPage={songsPagination.current_page}
                          totalItems={songsPagination.total}
                          itemsPerPage={songsPagination.per_page}
                          onPageChange={setSongsPage}
                          position="bottom"
                        />
                      </>
                    )}
                  </Card.Body>
                </Card>
              </Tab.Pane>
              <Tab.Pane eventKey="referred-devices">
                <Card>
                  <Card.Body>
                    {referredDevicesError && <Alert variant="danger">{referredDevicesError}</Alert>}
                    {referredDevicesLoading ? (
                      <div className="text-center py-4"><Spinner animation="border" /> Loading referred devices...</div>
                    ) : (
                      <>
                        <Pagination
                          currentPage={referredDevicesPage}
                          totalItems={referredDevices.length}
                          itemsPerPage={REFERRED_DEVICES_PER_PAGE}
                          onPageChange={setReferredDevicesPage}
                          position="top"
                        />
                        <Table responsive hover size="sm">
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>Referee Device ID</th>
                              <th>Referee Alias ID</th>
                              <th>Reward</th>
                              <th>Claimed at</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {referredDevices.length === 0 ? (
                              <tr>
                                <td colSpan="6" className="text-center text-muted py-3">No referred devices.</td>
                              </tr>
                            ) : (
                              referredDevices
                                .slice(
                                  (referredDevicesPage - 1) * REFERRED_DEVICES_PER_PAGE,
                                  referredDevicesPage * REFERRED_DEVICES_PER_PAGE
                                )
                                .map((rd, idx) => (
                                  <tr key={rd.referee_device_id || idx}>
                                    <td>{(referredDevicesPage - 1) * REFERRED_DEVICES_PER_PAGE + idx + 1}</td>
                                    <td style={{ wordBreak: 'break-all' }}>
                                      <code className="small">{rd.referee_device_id || '—'}</code>
                                      <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        className="ms-1"
                                        onClick={() => copyToClipboard(rd.referee_device_id)}
                                        title="Copy"
                                      >
                                        <FontAwesomeIcon icon={faCopy} />
                                      </Button>
                                    </td>
                                    <td><code className="small">{rd.referee_alias_id || '—'}</code></td>
                                    <td><Badge bg="success">{rd.reward ?? 0} credits</Badge></td>
                                    <td>{formatDate(rd.claimed_at)}</td>
                                    <td>
                                      <Button
                                        variant="outline-primary"
                                        size="sm"
                                        onClick={() => navigate(`/devices/detail?device_id=${encodeURIComponent(rd.referee_device_id)}`)}
                                      >
                                        View
                                      </Button>
                                    </td>
                                  </tr>
                                ))
                            )}
                          </tbody>
                        </Table>
                        <Pagination
                          currentPage={referredDevicesPage}
                          totalItems={referredDevices.length}
                          itemsPerPage={REFERRED_DEVICES_PER_PAGE}
                          onPageChange={setReferredDevicesPage}
                          position="bottom"
                        />
                      </>
                    )}
                  </Card.Body>
                </Card>
              </Tab.Pane>
            </Tab.Content>
          </Tab.Container>
        </>
      )}

      {/* Update Credit (Bonus) Modal */}
      <Modal show={showUpdateCreditModal} onHide={closeUpdateCreditModal} centered scrollable fullscreen="sm-down">
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faCoins} className="me-2" />
            Update Bonus Credit
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleAdjustBonusCreditSubmit}>
          <Modal.Body>
            {updateCreditError && (
              <Alert variant="danger" dismissible onClose={() => setUpdateCreditError(null)}>
                {updateCreditError}
              </Alert>
            )}
            {updateCreditSuccess && (
              <Alert variant="success">{updateCreditSuccess}</Alert>
            )}
            <Form.Group className="mb-3">
              <Form.Label>Bonus type</Form.Label>
              <Form.Select
                value={updateCreditForm.bonus_type}
                onChange={(e) => setUpdateCreditForm((f) => ({ ...f, bonus_type: e.target.value }))}
                required
                disabled={bonusTypesLoading}
              >
                <option value="">Select type...</option>
                {bonusTypes.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </Form.Select>
              {bonusTypesLoading && <Spinner animation="border" size="sm" className="mt-1" />}
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Amount (positive = add, negative = subtract)</Form.Label>
              <InputGroup>
                <Button
                  variant="outline-secondary"
                  type="button"
                  onClick={() => {
                    const n = parseInt(updateCreditForm.amount, 10) || 0;
                    setUpdateCreditForm((f) => ({ ...f, amount: String(n - 1) }));
                  }}
                  disabled={updateCreditSubmitting}
                >
                  <FontAwesomeIcon icon={faMinus} />
                </Button>
                <Form.Control
                  type="number"
                  placeholder="e.g. 100 or -20"
                  value={updateCreditForm.amount}
                  onChange={(e) => setUpdateCreditAmount(e.target.value)}
                  min="-999999"
                  max="999999"
                  step="1"
                  required
                  disabled={updateCreditSubmitting}
                />
                <Button
                  variant="outline-secondary"
                  type="button"
                  onClick={() => {
                    const n = parseInt(updateCreditForm.amount, 10) || 0;
                    setUpdateCreditForm((f) => ({ ...f, amount: String(n + 1) }));
                  }}
                  disabled={updateCreditSubmitting}
                >
                  <FontAwesomeIcon icon={faPlus} />
                </Button>
              </InputGroup>
              <Form.Text className="text-muted">
                Current bonus: {device && device.balance && typeof device.balance === 'object' ? (device.balance.bonus_credits ?? 0) : 0} credits. You cannot subtract more than this.
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeUpdateCreditModal} disabled={updateCreditSubmitting}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={updateCreditSubmitting || bonusTypesLoading}>
              {updateCreditSubmitting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-1" />
                  Submitting...
                </>
              ) : (
                'Submit'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Referral Claim Modal */}
      <Modal show={showReferralClaimModal} onHide={closeReferralClaimModal} centered scrollable fullscreen="sm-down">
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faGift} className="me-2" />
            Add referral code
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleReferralClaimSubmit}>
          <Modal.Body>
            <div className="mb-3">
              <div className="text-muted mb-1">Referee device_id</div>
              <code className="small" style={{ wordBreak: 'break-all' }}>{deviceId}</code>
            </div>

            {hasUsedReferral && (
              <Alert variant="warning">
                This device already claimed a referral code.
              </Alert>
            )}
            {referralClaimError && (
              <Alert variant="danger" dismissible onClose={() => setReferralClaimError(null)}>
                {referralClaimError}
              </Alert>
            )}
            {referralClaimSuccess && (
              <Alert variant="success">
                {referralClaimSuccess}
              </Alert>
            )}

            <Form.Group className="mb-3">
              <Form.Label>Referral code</Form.Label>
              <Form.Control
                type="text"
                value={referralClaimCode}
                onChange={(e) => setReferralClaimCode(e.target.value)}
                placeholder="Enter referral code (e.g. ABC12XYZ)"
                disabled={referralClaimSubmitting || hasUsedReferral}
                autoFocus
              />
            </Form.Group>

            {referralClaimData?.balances && (
              <div className="mt-3">
                <div className="fw-semibold mb-2">Balances (from claim response)</div>
                {renderClaimBalance('Referee', referralClaimData.balances.referee)}
                {renderClaimBalance('Referrer', referralClaimData.balances.referrer)}
                {referralClaimData.balances.referrer?.device_id && (
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => navigate(`/devices/detail?device_id=${encodeURIComponent(referralClaimData.balances.referrer.device_id)}`)}
                  >
                    Open referrer device
                  </Button>
                )}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeReferralClaimModal} disabled={referralClaimSubmitting}>
              Close
            </Button>
            <Button
              variant="warning"
              type="submit"
              disabled={!referralClaimCode.trim() || referralClaimSubmitting || hasUsedReferral}
            >
              {referralClaimSubmitting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-1" />
                  Claiming...
                </>
              ) : (
                'Claim'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default DeviceDetail;
